import { createContext, useContext, useState, ReactNode, useEffect } from "react";

export type Client = {
  id: string;
  companyName: string;
  country: string;
  address: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  email?: string;
  phone?: string;
  companyWebsite?: string;
  industry?: string;
  businessType?: string;
  selectedUsers?: string[];
  setupCompleted?: boolean;
  setupProgress?: {
    companyInfo: boolean;
    locations: boolean;
    teamMembers: boolean;
    floorPlans: boolean;
    assetCategories: boolean;
    assets: boolean;
  };
  createdAt?: string;
};

type ClientContextType = {
  clients: Client[];
  selectedClient: Client | null;
  addClient: (client: Omit<Client, "id" | "createdAt">) => Client;
  setClientFromLogin: (companyId: string, companyName: string, email?: string, phone?: string, hasLocations?: boolean) => void;
  selectClient: (clientId: string) => void;
  updateClient: (clientId: string, updates: Partial<Client>) => void;
  deleteClient: (clientId: string) => void;
};

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const ClientProvider = ({ children }: { children: ReactNode }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

  // Load clients from localStorage on mount
  useEffect(() => {
    const storedClients = localStorage.getItem("clients");
    const storedSelectedClientId = localStorage.getItem("selectedClientId");
    
    if (storedClients) {
      const parsedClients = JSON.parse(storedClients);
      setClients(parsedClients);
      
      // If there's a stored selected client ID, use it
      if (storedSelectedClientId && parsedClients.find((c: Client) => c.id === storedSelectedClientId)) {
        setSelectedClientId(storedSelectedClientId);
      } else if (parsedClients.length > 0) {
        // Otherwise, select the first client
        setSelectedClientId(parsedClients[0].id);
      }
    }
  }, []);

  // Save clients to localStorage whenever they change
  useEffect(() => {
    if (clients.length > 0) {
      localStorage.setItem("clients", JSON.stringify(clients));
    }
  }, [clients]);

  // Save selected client ID to localStorage
  useEffect(() => {
    if (selectedClientId) {
      localStorage.setItem("selectedClientId", selectedClientId);
    }
  }, [selectedClientId]);

  const addClient = (clientData: Omit<Client, "id" | "createdAt" | "setupCompleted" | "setupProgress">): Client => {
    const newClient: Client = {
      ...clientData,
      id: `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      setupCompleted: false,
      setupProgress: {
        companyInfo: false,
        locations: false,
        teamMembers: false,
        floorPlans: false,
        assetCategories: false,
        assets: false,
      },
    };
    
    setClients((prev) => [...prev, newClient]);
    setSelectedClientId(newClient.id);
    
    return newClient;
  };

  const selectClient = (clientId: string) => {
    if (clients.find((c) => c.id === clientId)) {
      setSelectedClientId(clientId);
    }
  };

  const updateClient = (clientId: string, updates: Partial<Client>) => {
    setClients((prev) =>
      prev.map((client) =>
        client.id === clientId ? { ...client, ...updates } : client
      )
    );
  };

  const setClientFromLogin = (companyId: string, companyName: string, email?: string, phone?: string, hasLocations?: boolean) => {
    // Remove any default company
    setClients((prev) => {
      const filtered = prev.filter((client) => client.companyName !== "Default Company");
      
      // Check if company already exists
      const existingClient = filtered.find((c) => c.id === companyId);
      
      // Determine setup state based on hasLocations
      // If hasLocations is true: company has locations, so setup is complete (go to main dashboard)
      // If hasLocations is false: company has no locations, show location setup step
      const setupCompleted = hasLocations === true;
      const setupProgress = {
        companyInfo: true, // Company info is provided from super admin
        locations: hasLocations === true, // If has locations, this step is complete
        teamMembers: hasLocations === true, // Skip team members if has locations
        floorPlans: false,
        assetCategories: false,
        assets: false,
      };
      
      if (existingClient) {
        // Update existing client with new data including email, phone, and setup state
        const updated = filtered.map((client) =>
          client.id === companyId
            ? { 
                ...client, 
                companyName, 
                email: email || client.email, 
                phone: phone || client.phone,
                setupCompleted,
                setupProgress,
              }
            : client
        );
        setSelectedClientId(companyId);
        return updated;
      } else {
        // Add new client from login
        const newClient: Client = {
          id: companyId,
          companyName,
          country: "",
          address: "",
          city: "",
          state: "",
          zip: "",
          email: email || "",
          phone: phone || "",
          createdAt: new Date().toISOString(),
          setupCompleted,
          setupProgress,
        };
        setSelectedClientId(companyId);
        return [...filtered, newClient];
      }
    });
  };

  const deleteClient = (clientId: string) => {
    setClients((prev) => prev.filter((client) => client.id !== clientId));
    if (selectedClientId === clientId) {
      const remainingClients = clients.filter((client) => client.id !== clientId);
      if (remainingClients.length > 0) {
        setSelectedClientId(remainingClients[0].id);
      } else {
        setSelectedClientId(null);
      }
    }
  };

  const selectedClient = clients.find((c) => c.id === selectedClientId) || null;

  return (
    <ClientContext.Provider
      value={{
        clients,
        selectedClient,
        addClient,
        setClientFromLogin,
        selectClient,
        updateClient,
        deleteClient,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
};

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error("useClient must be used within ClientProvider");
  }
  return context;
};

